import { INaceActivity } from "../../../types";
import { FormConfig } from "./types";
import axios from "axios";

const collateralTypes = ["საცხოვრებელი უძრავი ქონება", "კომერციული უძრავი ქონება", "მანქანა-დანადგარები", "მოძრავი ქონება", "ფასიანი ქაღალდები", "სხვა"];
const projectTypes = ["გაფართოება", "ახალი"];
const territoryUsageTypes = ["საკუთრება", "დროებით სარგებლობა"];

const checkDefaultedCompany = async (identificationCode: string) => {
  try {
    await axios.get(`/check_defaulted_company/${identificationCode}`);
    return null; // No error means not defaulted
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data.message;
    }
    return "შეცდომა დეფოლტის შემოწმებისას";
  }
};

const checkDebtorRegistry = async (identificationCode: string) => {
  try {
    await axios.get(`/check_debtor_registry/${identificationCode}`);
    return null; // No error means not in debtor registry
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data.message;
    }
    return "შეცდომა დავალიანებათა რეესტრში შემოწმებისას";
  }
};

const checkConsultingCenterMember = async (identificationCode: string): Promise<boolean> => {
  try {
    if (process.env.REACT_APP_ENVIRONMENT === "development") return true;

    const response = await axios.get(`/check_consulting_center_member/${identificationCode}`);
    return response.data.is_member === true;
  } catch (error) {
    console.error("Error checking consulting center membership:", error);
    return false;
  }
};

const fetchMunicipalities = async (region: string): Promise<Array<{ value: string; label: string }>> => {
  if (!region) {
    return [];
  }
  try {
    const response = await axios.post<{ _id: string; municipality: string }[]>("/fetch_areas", { region });
    return response.data.map((m) => ({ value: m._id, label: m.municipality }));
  } catch (error) {
    console.error("Error fetching municipalities:", error);
    return [];
  }
};

const fetchAndFilterSettlements = async (data: any, generalInfo: any): Promise<Array<{ value: string; label: string }>> => {
  const selectedRegion = data?.project_information?.actual_address?.region;
  const selectedMunicipalityId = data?.project_information?.actual_address?.municipality;
  const defaultOption = [{ value: "undefined", label: "არ არის მაღალმთიანი" }];

  if (!selectedMunicipalityId || !selectedRegion || !generalInfo?.mountainous_settlements) {
    return defaultOption;
  }

  try {
    const regionMunicipalities = await fetchMunicipalities(selectedRegion);
    const selectedMunicipality = regionMunicipalities.find((m) => m.value === selectedMunicipalityId);

    if (!selectedMunicipality) {
      return defaultOption;
    }

    const selectedMunicipalityName = selectedMunicipality.label;

    const filteredSettlements = generalInfo.mountainous_settlements
      .filter((s: any) => s.municipality === selectedMunicipalityName)
      .map((s: any) => ({ value: s._id, label: s.settlement })); // Only show settlement name now

    return [...defaultOption, ...filteredSettlements];
  } catch (error) {
    console.error("Error filtering mountainous settlements:", error);
    return defaultOption;
  }
};

const shouldApplyLocationRestriction = (formValues: Record<string, any> | undefined, generalInfo: any): boolean => {
  if (!formValues || !generalInfo?.universal_program_types) {
    return false;
  }
  const programComponentId = formValues?.program_type;
  const programTypes = generalInfo.universal_program_types;
  const selectedProgram = programTypes.find((p: any) => p._id === programComponentId);
  const isAgroTourism = selectedProgram?.name === "აგრო ტურიზმი";
  const isHotel = selectedProgram?.name === "სასტუმრო";

  const economicActivities = formValues?.project_information?.economic_activity;
  const naceActivities = generalInfo?.new_universal_nace_activities;
  const is5520Activity = isNaceCodeSelected(economicActivities, naceActivities, ["55.20.0"]);

  return isAgroTourism || is5520Activity || isHotel;
};

// Removed redundant isActivity5520 function, using isNaceCodeSelected instead

const checkRestrictedLocation = (selectedValue: string, options: Array<{ value: string; label: string }>, restrictedLabels: string[]): string => {
  const selectedOption = options.find((opt) => opt.value === selectedValue);
  if (selectedOption && restrictedLabels.includes(selectedOption.label)) {
    return "მოცემულ ტერიტორიულ ერთეულში არ ფინანსდება აღნიშნული საქმიანობა";
  }
  return "";
};

const createLocationValidator = (
  generalInfo: any,
  fetchOptionsFunction: (data: any, generalInfo?: any) => Promise<Array<{ value: string; label: string }>>,
  restrictedLabels: string[],
  isDefaultValue?: (value: string) => boolean
) => {
  return async (value: string, formValues?: Record<string, any>): Promise<string> => {
    if (!value || (isDefaultValue && isDefaultValue(value))) {
      return "";
    }

    if (!shouldApplyLocationRestriction(formValues, generalInfo)) {
      return "";
    }

    try {
      const options = await fetchOptionsFunction(formValues, generalInfo);
      return checkRestrictedLocation(value, options, restrictedLabels);
    } catch (error) {
      console.error("Error during location validation:", error);
      return "შეცდომა ვალიდაციისას";
    }
  };
};

const isNaceCodeSelected = (
  selectedActivityIds: (string | Partial<INaceActivity>)[] | undefined,
  naceActivities: any[] | undefined,
  targetCodes: string[]
): boolean => {
  if (!selectedActivityIds || !naceActivities || selectedActivityIds.length === 0) {
    return false;
  }
  return selectedActivityIds.some((id: string | Partial<INaceActivity>) => {
    const activity = naceActivities.find((a: any) => (typeof id === "string" ? a._id === id : a._id === id._id));
    return activity && targetCodes.includes(activity.activity_code?.trim());
  });
};

const getFormConfig = (generalInfo?: any, isLeasing?: boolean): FormConfig => ({
  groups: [
    {
      name: "application_type",
      label: "განაცხადის ტიპი",
    },
    {
      name: "guarantee",
      label: "უზრუნველყოფა",
      visibility: (data) => data.credit_guarantee_scheme && !isLeasing,
    },
    {
      name: "collaterals",
      label: "უზრუნველყოფის დამატება/რედაქტირება",
      visibility: (data) => data.credit_guarantee_scheme && !isLeasing,
      isMultiGroup: true,
    },
    {
      name: "borrower_info",
      label: "ინფორმაცია მსესხებლის შესახებ",
    },
    {
      name: "applicant_information.related_borrowers_group",
      label: "ურთიერთდაკავშირებული მსესხებელთა ჯგუფი",
      visibility: (data) => data.applicant_information?.is_related_borrowers_group === true,
      isMultiGroup: true,
    },
    {
      name: "signers",
      label: "წარმომადგენელი პირ(ებ)ი",
      isMultiGroup: true,
    },
    {
      name: "gender_statistics",
      label: "გენდერული სტატისტიკა",
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      name: "project_information_address",
      label: "ინფორმაცია პროექტის შესახებ",
    },
    {
      name: "disabilities",
      label: "ინფორმაცია შშმ პირთა პირობების შესახებ",
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      name: "world_bank",
      label: "რისკის კატეგორია",
    },
    {
      name: "project_information.economic_activity",
      label: "პროექტის შესახებ ზოგადი ინფორმაცია",
      isMultiGroup: true,
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      name: "refinancing_information",
      label: isLeasing ? "დასარეფინანსირებელი სალიზინგო საგნის შესახებ ინფორმაცია" : "დასარეფინანსირებელი სესხის შესახებ ინფორმაცია",
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name === "რეფინანსირება";
      },
    },
    { name: "loan_information", label: isLeasing ? "დამტკიცებული ლიზინგის შესახებ ინფორმაცია" : "დამტკიცებული სესხის შესახებ ინფორმაცია" },
    {
      name: "loan_information.conditions.purpose.subcategory",
      label: isLeasing ? "დამტკიცებული ლიზინგის შესახებ ინფორმაცია" : "დამტკიცებული სესხის შესახებ ინფორმაცია",
      isMultiGroup: true,
    },
    {
      name: "capital_expenses",
      label: "კაპიტალური ხარჯები",
      // only visible when purpose type is შერეული (კაპიტალური ხარჯები/საბრუნავი)
      visibility: (data) => {
        const normalizedPurposeType =
          typeof data?.loan_information?.conditions?.purpose?.type === "string"
            ? data.loan_information.conditions.purpose.type
            : data?.loan_information?.conditions?.purpose?.type?._id;
        const purposeType = generalInfo?.purpose_types.find((type: any) => type._id === normalizedPurposeType);
        return purposeType?.name === "შერეული (კაპიტალური ხარჯები/საბრუნავი)";
      },
    },
    {
      name: "fake_capital_expenses",
      label: "",
      visibility: () => false,
    },
    {
      name: "operating_expenses",
      label: "საბრუნავი საშუალებების დასაფინანსებლად",
      visibility: (data) => {
        const normalizedPurposeType =
          typeof data?.loan_information?.conditions?.purpose?.type === "string"
            ? data.loan_information.conditions.purpose.type
            : data?.loan_information?.conditions?.purpose?.type?._id;
        const purposeType = generalInfo?.purpose_types.find((type: any) => type._id === normalizedPurposeType);
        return purposeType?.name === "შერეული (კაპიტალური ხარჯები/საბრუნავი)";
      },
    },
    {
      name: "fake_operating_expenses",
      label: "",
      visibility: () => false,
    },
    {
      name: "transport_expenses",
      label: "ტრანსპორტის შეძენა/რემონტი",
      visibility: (data) => {
        const normalizedPurposeType =
          typeof data?.loan_information?.conditions?.purpose?.type === "string"
            ? data.loan_information.conditions.purpose.type
            : data?.loan_information?.conditions?.purpose?.type?._id;
        const purposeType = generalInfo?.purpose_types.find((type: any) => type._id === normalizedPurposeType);
        const restrictedNaceCodes = ["49.41.0", "49.42.0", "52.10.0", "52.21.0", "52.22.0", "52.23.0", "52.24.0", "52.29.0"];
        const selectedActivityIds = data?.project_information?.economic_activity;
        const allNaceActivities = generalInfo?.new_universal_nace_activities;
        const hasRestrictedNace = isNaceCodeSelected(selectedActivityIds, allNaceActivities, restrictedNaceCodes);

        return purposeType?.name === "შერეული (კაპიტალური ხარჯები/საბრუნავი)" && hasRestrictedNace;
      },
    },
    {
      name: "special_checkboxes",
      label: "დადასტურება",
      // This group is visible if any of its conditional checkboxes should be visible
      visibility: (data) => {
        const selectedIds = data?.project_information?.economic_activity;
        const allNace = generalInfo?.new_universal_nace_activities;
        return (
          isNaceCodeSelected(selectedIds, allNace, ["35.11.1", "35.11.2", "35.11.3"]) ||
          isNaceCodeSelected(selectedIds, allNace, ["49.39.0"]) ||
          isNaceCodeSelected(selectedIds, allNace, ["50.10.0", "50.20.0"])
        );
      },
      // Removed invalid 'dependencies' property
    },
    {
      name: "project_information_general",
      label: "პროექტის შესახებ ზოგადი ინფორმაცია",
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      name: "agro_tourism",
      label: "აგრო ტურიზმი",
      visibility: (data) => {
        const normalizedApplicationType = typeof data?.application_type === "string" ? data.application_type : data?.application_type?._id;
        const normalizedProgramType = typeof data?.program_type === "string" ? data.program_type : data?.program_type?._id;
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === normalizedApplicationType);
        const selectedProgramComponent = generalInfo?.universal_program_types.find((type: any) => type._id === normalizedProgramType);
        return applicationType?.name !== "რეფინანსირება" && selectedProgramComponent?.name === "აგრო ტურიზმი";
      },
    },
    {
      name: "project_information_hotel_information_industrial_hotel_component",
      label: "სასტუმროს შესახებ ინფორმაცია",
      visibility: (data) => {
        const normalizedApplicationType = typeof data?.application_type === "string" ? data.application_type : data?.application_type?._id;
        const normalizedProgramType = typeof data?.program_type === "string" ? data.program_type : data?.program_type?._id;
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === normalizedApplicationType);
        const selectedProgramComponent = generalInfo?.universal_program_types.find((type: any) => type._id === normalizedProgramType);
        return (
          applicationType?.name !== "რეფინანსირება" &&
          (selectedProgramComponent?.name === "სასტუმრო" ||
            (selectedProgramComponent?.name === "ინდუსტრიული კომპონენტი" &&
              isNaceCodeSelected(data?.project_information?.economic_activity, generalInfo?.new_universal_nace_activities, ["55.20.0"])))
        );
      },
    },
    {
      name: "project_information_hotel_information",
      label: "",
      visibility: (data) => {
        const normalizedApplicationType = typeof data?.application_type === "string" ? data.application_type : data?.application_type?._id;
        const normalizedProgramType = typeof data?.program_type === "string" ? data.program_type : data?.program_type?._id;
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === normalizedApplicationType);
        const selectedProgramComponent = generalInfo?.universal_program_types.find((type: any) => type._id === normalizedProgramType);
        return applicationType?.name !== "რეფინანსირება" && selectedProgramComponent?.name === "სასტუმრო";
      },
    },
    {
      name: "attachments",
      label: "მისამაგრებელი დოკუმენტ(ებ)ი",
    },
  ],

  fields: [
    {
      group: "application_type",
      name: "program_type",
      label: "პროგრამის კომპონენტი",
      required: true,
      type: "select",
      options: generalInfo?.universal_program_types
        ? generalInfo.universal_program_types.filter((t: any) => !t.legacy).map((t: any) => ({ value: t._id, label: t.name }))
        : [],
    },
    {
      group: "application_type",
      name: "application_type",
      label: "განაცხადის ტიპი",
      required: true,
      type: "select",
      options: generalInfo?.universal_application_types
        ? generalInfo.universal_application_types.map((t: any) => ({
            value: t._id,
            label: t.name,
            disabled: !(
              t.name === "სესხი" ||
              t.name === "პარალელური სესხი" ||
              t.name === "რეფინანსირება" ||
              (isLeasing && t.name === "სალიზინგო პროექტი") ||
              (isLeasing && t.name === "პარალელური სალიზინგო პროექტი")
            ),
          }))
        : [],
    },
    {
      group: "application_type",
      name: "credit_guarantee_scheme",
      label: "საკრედიტო საგარანტიო სქემით სარგებლობა",
      type: "checkbox",
      visibility: (data) => !isLeasing,
    },

    {
      group: "guarantee",
      name: "guarantee.percentage",
      label: "მოთხოვნილი გარანტია %",
      required: true,
      type: "number",
      validation: (value: number) => (value > 80 ? "გარანტიის პროცენტი არ უნდა აღემატებოდეს 80%-ს" : ""),
    },
    {
      group: "guarantee",
      name: "guarantee.guarantee_amount",
      label: "გარანტიის თანხობრივი მოცულობა ₾",
      type: "number",
      readonly: true,
      value: (formValues: Record<string, any>) => {
        const approved_amount = formValues.loan_information.conditions.approved_amount || 0;
        const percentage = Number(formValues.guarantee?.percentage) || 0;
        const calculatedGuarantee = (approved_amount * percentage) / 100;

        return Math.ceil(calculatedGuarantee * 1000) / 1000;
      },
      dependencies: ["guarantee.percentage", "loan_information.conditions.approved_amount"],
    },
    {
      group: "guarantee",
      name: "guarantee.category",
      label: "გარანტიის პროცენტის კატეგორია",
      type: "select",
      options: [
        { value: "0-60", label: "0-60% (საკომისიო - 1%)" },
        { value: "61-80", label: "61-80% (საკომისიო - 1.5%)" },
      ],
      readonly: true,
      value: (formValues: Record<string, any>) => (formValues.guarantee?.percentage > 60 ? "61-80" : "0-60"),
      dependencies: ["guarantee.percentage"],
    },
    {
      group: "guarantee",
      name: "guarantee.calculated_collateral_coverage_percent",
      label: "უზრუნველყოფის მაჩვენებელი %",
      type: "number",
      readonly: true,
      value: (formValues: Record<string, any>) => {
        const guaranteePercentage = Number(formValues.guarantee?.percentage) || 0;
        const approvedAmount = Number(formValues.loan_information?.conditions?.approved_amount) || 0;

        if (approvedAmount === 0) {
          return "0.00";
        }

        // Calculate sum of collateral liquidation values
        const collaterals = formValues.collaterals || [];
        const totalLiquidationValue = collaterals.reduce((sum: number, collateral: any) => {
          return sum + (Number(collateral.liquidation_value) || 0);
        }, 0);

        // Calculate collateral coverage percentage
        const collateralCoveragePercent = (totalLiquidationValue / approvedAmount) * 100;
        const totalCoverage = guaranteePercentage + collateralCoveragePercent;

        return Math.ceil(totalCoverage * 1000) / 1000;
      },
      dependencies: ["guarantee.percentage", "loan_information.conditions.approved_amount", "collaterals"],
      validation: (value: string, formValues?: Record<string, any>) => {
        const numericValue = Number(value);
        if (numericValue > 110) {
          return "უზრუნველყოფის ჯამური პროცენტი არ უნდა აღემატებოდეს 110%-ს";
        }
        return "";
      },
    },

    // Collateral fields (multi-group "collaterals")
    {
      group: "collaterals",
      name: "collaterals.id",
      label: "უზრუნველყოფის უნიკალური ID",
      type: "text",
    },
    {
      group: "collaterals",
      name: "collaterals.type",
      label: "უზრუნველყოფის ტიპი",
      type: "select",
      options: collateralTypes.map((t) => ({ value: t, label: t })),
    },
    {
      group: "collaterals",
      name: "collaterals.property_id",
      label: "ქონების იდენტიფიკატორი",
      type: "text",
    },
    {
      group: "collaterals",
      name: "collaterals.address",
      label: "მისამართი",
      type: "text",
    },
    {
      group: "collaterals",
      name: "collaterals.liquidation_value",
      label: "სალიკვიდაციო ღირებულება ₾",
      type: "number",
    },
    {
      group: "collaterals",
      name: "collaterals.market_value",
      label: "საბაზრო ღირებულება ₾",
      type: "number",
    },
    {
      group: "collaterals",
      name: "collaterals.description",
      label: "აღწერა",
      type: "text",
    },
    // Borrower information fields
    {
      group: "borrower_info",
      name: "applicant_information.legal_form",
      label: "სამართლებრივი ფორმა",
      required: true,
      type: "select",
      options: generalInfo?.legal_forms
        ? generalInfo.legal_forms
            .filter((legal_form: any) => legal_form.name !== "ი/მ" && legal_form.name !== "ფიზიკური პირი")
            .map((legal_form: any) => ({ value: legal_form._id, label: legal_form.name }))
        : [],
    },
    {
      group: "borrower_info",
      name: "applicant_information.name",
      label: "დასახელება",
      required: true,
      type: "text",
    },
    {
      group: "borrower_info",
      name: "applicant_information.identification_code",
      label: "საიდენტიფიკაციო კოდი",
      required: true,
      type: "text",
      validation: async (value: string) => {
        if (!value) return "";
        if (value.length !== 9 || !/^\d{9}$/.test(value)) {
          return "საიდენტიფიკაციო კოდი უნდა შედგებოდეს 9 ციფრისგან";
        }
        
        // Check debtor registry first
        const debtorRegistryError = await checkDebtorRegistry(value);
        if (debtorRegistryError) {
          return debtorRegistryError;
        }
        
        // Then check defaulted company
        const defaultedError = await checkDefaultedCompany(value);
        return defaultedError || "";
      },
    },
    {
      group: "borrower_info",
      name: "applicant_information.brand_name",
      label: "კომპანიის ბრენდული სახელი (არსებობის შემთხვევაში)",
      required: true,
      type: "text",
    },
    {
      group: "borrower_info",
      name: "applicant_information.social_media",
      label: "სოციალური მედიის გვერდი (მიუთითეთ არსებობის შემთხვევაში)",
      required: true,
      type: "text",
    },
    {
      group: "borrower_info",
      name: "applicant_information.legal_address",
      label: "იურიდიული მისამართი",
      required: true,
      type: "text",
    },
    {
      group: "borrower_info",
      name: "applicant_information.email",
      label: "საჯარო ელ.ფოსტა (ამონაწერის მიხედვით)",
      required: true,
      type: "text",
    },
    {
      group: "borrower_info",
      name: "applicant_information.is_female_entrepreneur",
      label:
        "ქალი მეწარმე (პროგრამის მიზნებისთვის, ქალ მეწარმედ მიიჩნევა მხოლოდ ის მეწარმე სუბიექტი, რომელსაც პროგრამაში ჩართვამდე არანაკლებ ბოლო 6 თვის მანძილზე ჰყავს 100%-იანი წილის მესაკუთრე ქალ(ებ)ი)",
      type: "checkbox",
    },

    {
      group: "borrower_info",
      name: "applicant_information.income_not_exceeding_40m",
      label: "მეწარმე სუბიექტის შემოსავალი არ აღემატება 40 000 000 ლარს",
      type: "checkbox",
      required: true,
    },
    {
      group: "borrower_info",
      name: "applicant_information.is_agency_consulting_center_member",
      label: "რეგისტრირებულია სააგენტოს საკონსულტაციო ცენტრის პროგრამულ უზრუნველყოფაში და არის სააგენტოს საკონსულტაციო ცენტრის წევრი",
      type: "checkbox",
      readonly: true,
      required: true,
      dependencies: ["applicant_information.identification_code", "applicant_information.is_agency_consulting_center_member"],
      value: async (formValues: Record<string, any>) => {
        // if NODE_ENV is development, always return true
        if (process.env.REACT_APP_ENVIRONMENT === "development") return true;

        const identificationCode = formValues.applicant_information?.identification_code;
        if (!identificationCode) return false;

        if (identificationCode.length >= 3) {
          return await checkConsultingCenterMember(identificationCode);
        }
        return false;
      },
      validation: (value: boolean) => {
        if (value === false) {
          return "კომპანია უნდა დარეგისტრირდეს საკონსულტაციო ცენტრის ვებ გვერდზე - https://growthhub.gov.ge/ka/crm/login";
        }
        return "";
      },
    },
    {
      group: "borrower_info",
      name: "applicant_information.consulting_center_notice",
      label: "",
      type: "notice",
      // only visible if !applicant_information.is_agency_consulting_center_member
      visibility: (data) => {
        return !data.applicant_information?.is_agency_consulting_center_member;
      },
      content: `<p style="margin: 5px 0">
          კომპანია უნდა დარეგისტრირდეს საკონსულტაციო ცენტრის ვებ გვერდზე - 
          <a href="https://growthhub.gov.ge/ka/crm/login" target="_blank" rel="noopener noreferrer">
            https://growthhub.gov.ge/ka/crm/login
          </a>
        </p>`,
    },
    {
      group: "borrower_info",
      name: "applicant_information.not_state_owned",
      label:
        "არ არის სახელმწიფოსა და მუნიციპალიტეტის, აგრეთვე სს საქართველოს განვითარების ფონდის მიერ პირდაპირი ან არაპირდაპირი წილობრივი მონაწილეობით შექმნილი იურიდიული პირი",
      type: "checkbox",
      required: true,
    },
    {
      group: "borrower_info",
      name: "applicant_information.employee_amount",
      label: "დასაქმებულთა არსებული რაოდენობა",
      type: "number",
      inputProps: { step: "1", min: "0" },
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      group: "borrower_info",
      name: "project_information.jobs_created",
      label: "ახალი სამუშაო ადგლები (ინვესტიციის ფარგლებში)",
      required: true,
      type: "number",
      inputProps: { step: "1", min: "0" },
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      group: "borrower_info",
      name: "applicant_information.tax_liabilities",
      label: "სახელმწიფოს მიმართ არ აქვს საგადასახადო დავალიანება, ან გადავადებული აქვს, ან ვალდებულება შეჩერებულია",
      type: "checkbox",
      required: true,
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      group: "borrower_info",
      name: "applicant_information.registered_debtor",
      label: "არ არის რეგისტრირებული მოვალეთა რეესტრში",
      type: "checkbox",
      required: true,
      visibility: (data) => {
        const applicationType = generalInfo?.universal_application_types.find((type: any) => type._id === data?.application_type);
        return applicationType?.name !== "რეფინანსირება";
      },
    },
    {
      group: "borrower_info",
      name: "applicant_information.is_related_borrowers_group",
      label: "ურთიერთდაკავშირებულ მსესხებელთა ჯგუფი",
      type: "checkbox",
    },
    {
      group: "borrower_info",
      name: "applicant_information.related_borrowers_group_notice",
      label: "",
      type: "notice",
      content: `<p style="margin: 5px 0; color: #da1a1aff; font-style: italic;">
          ურთიერთდაკავშირებულ მსესხებელთა ჯგუფი განისაზღვრება „კომერციულ ბანკებში რისკის პოზიციების კონცენტრაციისა და მსხვილი რისკების შესახებ დებულების დამტკიცების თაობაზე“ საქართველოს ეროვნული ბანკის პრეზიდენტის 2019 წლის 21 ნოემბრის № 228/04 ბრძანებით დამტკიცებული დებულებითა და მისი ინსტრუქციით.
        </p>`,
    },
    {
      group: "applicant_information.related_borrowers_group",
      name: "name",
      label: "იურიდიული პირის დასახელება",
      type: "text",
      required: true,
    },
    {
      group: "applicant_information.related_borrowers_group",
      name: "identification_code",
      label: "საიდენტიფიკაციო კოდი",
      type: "text",
      required: true,
    },
    // Signers fields (multi-group "signers")
    {
      group: "signers",
      name: "signers.first_name",
      label: "სახელი",
      type: "text",
      required: true,
    },
    {
      group: "signers",
      name: "signers.last_name",
      label: "გვარი",
      type: "text",
      required: true,
    },
    {
      group: "signers",
      name: "signers.position",
      label: "პოზიცია",
      type: "text",
      required: true,
    },
    {
      group: "signers",
      name: "signers.private_number",
      label: "პ/ნ",
      type: "text",
      required: true,
    },
    {
      group: "signers",
      name: "signers.phone_number",
      label: "ტელ. ნომერი",
      type: "text",
      required: true,
    },
    {
      group: "signers",
      name: "signers.email",
      label: "წარმომადგენლის ელ.ფოსტა",
      type: "text",
      required: true,
    },
    {
      group: "signers",
      name: "signers.is_poa_person",
      label: "მინდობილი პირი",
      type: "checkbox",
    },
    {
      group: "signers",
      name: "signers.poa_first_name",
      label: "მინდობილი პირის სახელი",
      type: "text",
      visibility: (data: any, index?: number) => {
        return typeof index === "number" && data.signers?.[index]?.is_poa_person === true;
      },
    },
    {
      group: "signers",
      name: "signers.poa_last_name",
      label: "მინდობილი პირის გვარი",
      type: "text",
      visibility: (data: any, index?: number) => {
        return typeof index === "number" && data.signers?.[index]?.is_poa_person === true;
      },
    },
    {
      group: "signers",
      name: "signers.poa_identification_code",
      label: "მინდობილი პირის პირადი ნომერი",
      type: "text",
      visibility: (data: any, index?: number) => {
        return typeof index === "number" && data.signers?.[index]?.is_poa_person === true;
      },
    },
    {
      group: "signers",
      name: "signers.poa_requisites",
      label: "მინდობილობის ნომერი",
      type: "text",
      visibility: (data: any, index?: number) => {
        return typeof index === "number" && data.signers?.[index]?.is_poa_person === true;
      },
    },
    // Gender Statistics Fields
    {
      group: "gender_statistics",
      name: "gender_statistics.director_women",
      label: "დირექტორი ქალ(ებ)ის რაოდენობა",
      type: "number",
      required: true,
      inputProps: { step: "1", min: "0" },
    },
    {
      group: "gender_statistics",
      name: "gender_statistics.founder_women",
      label: "დამფუძნებელი ქალ(ებ)ის რაოდენობა",
      type: "number",
      required: true,
      inputProps: { step: "1", min: "0" },
    },
    {
      group: "gender_statistics",
      name: "gender_statistics.stakes_women",
      label: "დამფუძნებელი ქალ(ებ)ის ჯამური წილობრივი მონაწილეობა (%)",
      type: "number",
      required: true,
    },
    {
      group: "project_information_address",
      name: "project_information.actual_address.region",
      label: "რეგიონი",
      required: true,
      type: "select",
      options: generalInfo?.regions ? generalInfo.regions.map((r: string) => ({ value: r, label: r })) : [],
    },
    {
      group: "project_information_address",
      name: "project_information.actual_address.municipality",
      label: "მუნიციპალიტეტი/თვითმმართველი ქალაქი",
      required: true,
      type: "select",
      options: async (data: any) => {
        return fetchMunicipalities(data?.project_information?.actual_address?.region);
      },
      dependencies: ["project_information.actual_address.region", "program_type", "project_information.economic_activity"],
      validation: createLocationValidator(generalInfo, async (formValues) => fetchMunicipalities(formValues?.project_information?.actual_address?.region), [
        "თბილისი",
        "ბათუმი",
      ]),
    },
    {
      group: "project_information_address",
      name: "project_information.actual_address.mountainous_settlement",
      label: "მაღალმთიანი დასახლება",
      type: "select",
      required: true,
      options: (data: any) => fetchAndFilterSettlements(data, generalInfo),
      dependencies: [
        "project_information.actual_address.region",
        "project_information.actual_address.municipality",
        "program_type",
        "project_information.economic_activity",
      ],
      // Corrected the isDefaultValue check to use the actual value "undefined"
      validation: createLocationValidator(generalInfo, fetchAndFilterSettlements, ["ბაკურიანი", "გუდაური"], (value) => value === "undefined"),
    },
    {
      group: "project_information_address",
      name: "project_information.actual_address.street",
      label: "მისამართი (სოფელი/დაბა/ქუჩა/ნომერი)",
      required: true,
      type: "text",
    },
    {
      group: "project_information_address",
      name: "project_information.actual_address.cadastral_code",
      label: "საკადასტრო კოდი",
      required: true,
      type: "text",
    },
    {
      group: "project_information_address",
      name: "green_taxonomy.classified_as_green_loan",
      label: "სესხის/ლიზინგის თანხა სრულად/ნაწილობრივ მიმართული იქნება ელექტრო სადგურის მოსაწყობად",
      type: "checkbox",
      value: (formValues: Record<string, any>) => {
        // If did_not_pass_screening is true, this should be false
        if (formValues.green_taxonomy?.did_not_pass_screening === true) {
          return false;
        }
        return formValues.green_taxonomy?.classified_as_green_loan;
      },
      dependencies: ["green_taxonomy.passed_screening", "green_taxonomy.did_not_pass_screening"],
    },
    {
      group: "project_information_address",
      name: "green_taxonomy.passed_screening",
      label: "სებ-ის მწვანე ტაქსონომიის მიხედვით შესაძლოა კვალიფიცირდებოდეს მწვანე სესხად",
      type: "checkbox",
      value: (formValues: Record<string, any>) => {
        // If classified_as_green_loan is true, this should be true
        if (formValues.green_taxonomy?.classified_as_green_loan === true) {
          return true;
        }
        // If did_not_pass_screening is true, this should be false
        if (formValues.green_taxonomy?.did_not_pass_screening === true) {
          return false;
        }
        return formValues.green_taxonomy?.passed_screening;
      },
      dependencies: ["green_taxonomy.classified_as_green_loan", "green_taxonomy.did_not_pass_screening"],
    },
    {
      group: "project_information_address",
      name: "green_taxonomy.did_not_pass_screening",
      label: "სებ-ის მწვანე ტაქსონომიის მიხედვით შესაძლოა არ კვალიფიცირდებოდეს მწვანე სესხად",
      type: "checkbox",
      value: (formValues: Record<string, any>) => {
        // If any other green taxonomy option is true, this should be false
        if (formValues.green_taxonomy?.classified_as_green_loan === true || formValues.green_taxonomy?.passed_screening === true) {
          return false;
        }
        return formValues.green_taxonomy?.did_not_pass_screening;
      },
      dependencies: ["green_taxonomy.classified_as_green_loan", "green_taxonomy.passed_screening"],
    },

    // Disability-related fields
    {
      group: "disabilities",
      name: "disabilities.disabled_employees",
      label: "არსებული, დასაქმებული შშმ პირების რაოდენობა",
      type: "number",
      inputProps: { step: "1", min: "0" },
    },
    {
      group: "disabilities",
      name: "disabilities.is_adapted",
      label: "ადმინისტრაციული შენობა ადაპტირებულია",
      type: "checkbox",
      readonly: (formValues: Record<string, any>) => formValues.disabilities?.is_planned_to_adapt === true,
      value: (formValues: Record<string, any>) => {
        // When is_planned_to_adapt becomes true, automatically uncheck is_adapted
        if (formValues.disabilities?.is_planned_to_adapt === true) {
          return false;
        }
        // Otherwise, keep its current value
        return formValues.disabilities?.is_adapted;
      },
    },
    {
      group: "disabilities",
      name: "disabilities.is_planned_to_adapt",
      label: "იგეგმება ადაპტირება",
      type: "checkbox",
      readonly: (formValues: Record<string, any>) => formValues.disabilities?.is_adapted === true,
      value: (formValues: Record<string, any>) => {
        // When is_adapted becomes true, automatically uncheck is_planned_to_adapt
        if (formValues.disabilities?.is_adapted === true) {
          return false;
        }
        // Otherwise, keep its current value
        return formValues.disabilities?.is_planned_to_adapt;
      },
    },

    // WorldBank-related fields
    {
      group: "world_bank",
      name: "world_bank.banned_activities_disclaimer",
      label: "არ ეწევა პროგრამით გათვალისწინებულ აკრძალულ საქმიანობებს",
      type: "checkbox",
    },
    {
      group: "world_bank",
      name: "world_bank.permits",
      label:
        "ლიცენზიებისა და ნებართვების შესახებ საქართველოს კანონის შესაბამისად, საჭიროების შემთხვევაში, ფლობს საქმიანობის განხორციელების ან/და მშენებლობისთვის საჭირო ყველა ლიცენზიას და ნებართვას",
      type: "checkbox",
    },
    {
      group: "world_bank",
      name: "world_bank.permits_notice",
      label: "",
      type: "notice",
      content: `<p style="margin: 5px 0">
          *გთხოვთ, რისკების კატეგორიის არჩევისას იხელმძღვანელოთ
          <a href="https://matsne.gov.ge/ka/document/view/3691981?publication=10" target="_blank" rel="noopener noreferrer">
            საქართველოს გარემოსდაცვითი შეფასების კოდექსის პირველი და მეორე დანართებით.
          </a>
        </p>`,
    },
    {
      group: "world_bank",
      name: "world_bank.risk_category",
      label: "მსოფლიო ბანკის რისკების კატეგორია",
      type: "select",
      options: [
        { value: "moderate", label: "მცირე ან ზომიერი (Low or moderate)" },
        { value: "substantial", label: "მნიშვნელოვანი (Substantial)" },
        { value: "high", label: "მაღალი (High)" },
      ],
      // default to moderate
      value: (formValues: Record<string, any>) => formValues.world_bank?.risk_category || "moderate",
    },
    {
      group: "world_bank",
      name: "world_bank.risk_category_notice",
      label: "",
      type: "notice",
      content: (formValues: Record<string, any>) => {
        const riskCategory = formValues.world_bank?.risk_category;
        if (riskCategory === "substantial") {
          return "*მეწარმე სუბიექტის საქმიანობა, რომელიც მოცემულია საქართველოს გარემოსდაცვითი შეფასების კოდექსის მე-2 დანართში";
        } else if (riskCategory === "high") {
          return "*მიეკუთვნება საქმიანობებს, რომლებიც მოცემულია საქართველოს გარემოსდაცვითი შეფასების კოდექსის პირველ დანართში";
        } else if (riskCategory === "moderate") {
          return "*მეწარმე სუბიექტის საქმიანობა არ კვალიფიცირდება მნიშვნელოვანი და მაღალი რისკის მქონე საქმიანობებად, არ არის საქართველოს გარემოსდაცვითი შეფასების კოდექსის პირველი და მეორე დანართებით განსაზღვრული საქმიანობების ჩამონათვალში და არ გააჩნია ბუნებრივ და სოციალურ გარემოზე უარყოფითი ზემოქმედების პოტენციალი";
        }
        return "";
      },
      dependencies: ["world_bank.risk_category"],
    },
    {
      group: "world_bank",
      name: "world_bank.subsidy_mechanism",
      label: "მსოფლიო ბანკიდან დაფინანსებული პროექტი",
      type: "checkbox",
      visibility: (data) => data.world_bank?.risk_category === "moderate" || data.world_bank?.risk_category === "substantial",
      readonly: (formValues: Record<string, any>) =>
        !formValues.world_bank?.banned_activities_disclaimer || !formValues.world_bank?.permits || formValues.world_bank?.subsidy_mechanism,
      value: (formValues: Record<string, any>) => {
        // If any of these conditions are true, subsidy_mechanism should be false
        if (
          formValues.world_bank?.government_subsidy_mechanism === true ||
          !formValues.world_bank?.banned_activities_disclaimer ||
          !formValues.world_bank?.permits
        ) {
          return false;
        }
        // Otherwise, keep its current value
        return formValues.world_bank?.subsidy_mechanism;
      },
      dependencies: ["world_bank.banned_activities_disclaimer", "world_bank.permits", "world_bank.government_subsidy_mechanism"],
    },
    {
      group: "world_bank",
      name: "world_bank.government_subsidy_mechanism",
      label: "სახელმწიფო სუბსიდირების მექანიზმი",
      type: "checkbox",
      readonly: (formValues: Record<string, any>) => formValues.world_bank?.subsidy_mechanism === true,
      value: (formValues: Record<string, any>) => {
        // If subsidy_mechanism is true, then government_subsidy_mechanism must be false
        if (formValues.world_bank?.subsidy_mechanism === true) {
          return false;
        }
        // Otherwise, keep its current value
        return formValues.world_bank?.government_subsidy_mechanism;
      },
      dependencies: ["world_bank.subsidy_mechanism"],
    },
    {
      group: "world_bank",
      name: "world_bank.subsidy_mechanism_absence_comment",
      label: "მიზეზი, რატომ არ იყენებს მსოფლიო ბანკის დაფინანსებას",
      type: "text",
      visibility: (data) =>
        (data.world_bank?.risk_category === "moderate" || data.world_bank?.risk_category === "substantial") &&
        data.world_bank?.government_subsidy_mechanism === true,
      value: (formValues: Record<string, any>) => {
        const isGovernmentSubsideMechanism = formValues.world_bank?.government_subsidy_mechanism === true;
        if (!isGovernmentSubsideMechanism) {
          return "";
        }
        return formValues.world_bank?.subsidy_mechanism_absence_comment;
      },
      dependencies: ["world_bank.government_subsidy_mechanism"],
    },
    {
      group: "world_bank",
      name: "world_bank.eia_status",
      label: "მსოფლიო ბანკიდან დაფინანსებული პროექტი",
      type: "select",
      visibility: (data) => data.world_bank?.subsidy_mechanism === true && data.world_bank?.risk_category === "substantial",
      options: [
        { value: "eia_exists", label: "აქვს გზშ" },
        { value: "eia_abscence_with_letter", label: "აქვს სკრინინგის გადაწყვეტილება გეს-იდან" },
      ],
    },
    // Refinancing Information Fields
    {
      group: "refinancing_information",
      name: "refinancing_information.initial_bank",
      label: "ბანკი (საწყისი)",
      type: "select",
      required: true,
      options: generalInfo?.banks ? generalInfo.banks.map((bank: any) => ({ value: bank._id, label: bank.name })) : [],
    },
    {
      group: "refinancing_information",
      name: "refinancing_information.loan_id",
      label: isLeasing ? "დასარეფინანსირებელი ლიზინგის ID" : "დასარეფინანსირებელი სესხის ID",
      type: "text",
      required: true,
    },
    {
      group: "refinancing_information",
      name: "refinancing_information.first_disbursment_date",
      label: "სესხის პირველი ტრანშის გაცემის თარიღი",
      type: "date",
      required: true,
    },
    {
      group: "refinancing_information",
      name: "refinancing_information.approved_amount",
      label: "დამტკიცებული თანხა (GEL)",
      type: "number",
      required: true,
    },
    {
      group: "refinancing_information",
      name: "refinancing_information.remaining_amount",
      label: "ნაშთი (დარჩენილი ძირითადი თანხა) GEL",
      type: "number",
      required: true,
    },
    {
      group: "refinancing_information",
      name: "refinancing_information.outstanding_amount",
      label: "დარჩენილი ასათვისებელი მოცულობა (GEL) (არსებობის შემთხვევაში)",
      type: "number",
      required: true,
    },

    {
      group: "loan_information",
      name: "loan_information.loan_id",
      label: isLeasing ? "ლიზინგის ID" : "სესხის ID",
      type: "text",
      required: true,
    },
    {
      group: "loan_information",
      name: "loan_information.conditions.approval_date",
      label: isLeasing ? "ლიზინგის დამტკიცების თარიღი" : "სესხის დამტკიცების თარიღი",
      type: "date",
      required: true,
      validation: (value: string) => {
        if (!value) return "";
        const selectedDate = new Date(value);
        const today = new Date();
        const twentyYearsAgo = new Date();
        twentyYearsAgo.setFullYear(today.getFullYear() - 20);
        const twentyYearsLater = new Date();
        twentyYearsLater.setFullYear(today.getFullYear() + 20);
        if (selectedDate < twentyYearsAgo || selectedDate > twentyYearsLater) {
          return "თარიღი არ არის ვალიდური";
        }
        return "";
      },
    },
    {
      group: "loan_information",
      name: "loan_information.conditions.approved_amount",
      label: "დამტკიცებული თანხა (GEL)",
      type: "number",
      required: true,
      validation: (value: number) => {
        if (!value) return "";
        if (value < 50000) {
          return "დამტკიცებული თანხა უნდა იყოს მინიმუმ 50,000 ლარი";
        }
        if (value > 10000000) {
          return "დამტკიცებული თანხა არ უნდა აღემატებოდეს 10,000,000 ლარს";
        }
        return "";
      },
    },
    {
      group: "loan_information",
      name: "loan_information.conditions.approval_months",
      label: "დამტკიცებული ვადა (თვე)",
      type: "number",
      required: true,
      inputProps: { step: "1", min: "0" },
    },
    {
      group: "loan_information",
      name: "loan_information.conditions.interest_rate_type",
      label: "დამტკიცებული საპროცენტო განაკვეთის ტიპი",
      type: "select",
      options: generalInfo?.interest_types ? generalInfo.interest_types.map((type: any) => ({ value: type._id, label: type.name })) : [],
      required: true,
    },
    // loan_information.conditions.purpose.refinancing_rate
    // ე.ბ.რ.გ. %
    {
      group: "loan_information",
      name: "loan_information.conditions.purpose.refinancing_rate",
      label: "ე.ბ.რ.გ. %",
      type: "number",
      required: true,
    },
    // if interest rate type name is "ცვლადი", display field
    // loan_information.conditions.purpose.variable_rate - ბანკის მარჟა %
    // (Use ._id to get the title of the interest rate type)
    {
      group: "loan_information",
      name: "loan_information.conditions.purpose.variable_rate",
      label: "ბანკის მარჟა %",
      type: "number",
      required: true,
      visibility: (data) => {
        const interestRateType = generalInfo?.interest_types.find((type: any) => type._id === data?.loan_information?.conditions?.interest_rate_type);
        return interestRateType?.name === "ცვლადი";
      },
    },
    {
      group: "loan_information",
      name: "loan_information.conditions.purpose.interest_rate",
      label: "საპროცენტო განაკვეთი %",
      type: "number",
      required: true,
      // If interest rate type name is "ცვლადი", this should be readOnly
      // and value should be calculated as variable_rate + refinancing_rate
      readonly: (formValues: Record<string, any>) => {
        const interestRateType = generalInfo?.interest_types.find((type: any) => type._id === formValues?.loan_information?.conditions?.interest_rate_type);
        return interestRateType?.name === "ცვლადი";
      },
      value: (formValues: Record<string, any>) => {
        const interestRateType = generalInfo?.interest_types.find((type: any) => type._id === formValues?.loan_information?.conditions?.interest_rate_type);
        if (interestRateType?.name === "ცვლადი") {
          return Number(formValues.loan_information.conditions.purpose.variable_rate) + Number(formValues.loan_information.conditions.purpose.refinancing_rate);
        }
        return formValues.loan_information.conditions.purpose.interest_rate;
      },
      dependencies: [
        "loan_information.conditions.purpose.variable_rate",
        "loan_information.conditions.purpose.refinancing_rate",
        "loan_information.conditions.interest_rate_type",
      ],
    },
    // If interest type is "შერეული" show loan_information.conditions.purpose.mixed_interest_rate (შერეული (კომენტარი))

    {
      group: "loan_information",
      name: "loan_information.conditions.purpose.mixed_interest_rate",
      label: "შერეული (კომენტარი)",
      type: "text",
      visibility: (data) => {
        const interestRateType = generalInfo?.interest_types.find((type: any) => type._id === data?.loan_information?.conditions?.interest_rate_type);
        return interestRateType?.name === "შერეული";
      },
    },
    {
      group: "loan_information",
      name: "loan_information.conditions.purpose.agency_cofinancing_type",
      label: "სააგენტოს თანადაფინანსების ტიპი",
      type: "select",
      options: async (formValues: Record<string, any>) => {
        const targetNaceCodes = ["38.11.0", "38.12.0", "38.21.0", "38.22.0", "38.31.0", "38.32.0"];
        const selectedActivityIds = formValues?.project_information?.economic_activity;
        const allNaceActivities = generalInfo?.new_universal_nace_activities;
        const hasSpecialNace = isNaceCodeSelected(selectedActivityIds, allNaceActivities, targetNaceCodes);

        const loanOptions = [
          { value: "სესხის ვადით, ს.ე.ბ.რ.გ. - 3%", label: "სესხის ვადით, ს.ე.ბ.რ.გ. - 3%" },
          { value: "სესხის ვადით, ს.ე.ბ.რ.გ. ოდენობით", label: "სესხის ვადით, ს.ე.ბ.რ.გ. ოდენობით" },
        ];

        const leasingOptions = [
          { value: "ლიზინგის ვადით, ს.ე.ბ.რ.გ. - 1%", label: "ლიზინგის ვადით, ს.ე.ბ.რ.გ. - 1%" },
          { value: "სალიზინგო პროექტის ვადით, ს.ე.ბ.რ.გ. + 2 %", label: "სალიზინგო პროექტის ვადით, ს.ე.ბ.რ.გ. + 2 %" },
        ];

        const baseOptions = isLeasing ? leasingOptions : loanOptions;

        if (hasSpecialNace) {
          return [baseOptions[1]]; // Only show the special option
        }
        return [baseOptions[0]]; // Only show the general option
      },
      required: true,
      dependencies: ["project_information.economic_activity"],
      value: (formValues: Record<string, any>) => {
        const targetNaceCodes = ["38.11.0", "38.12.0", "38.21.0", "38.22.0", "38.31.0", "38.32.0"];
        const selectedActivityIds = formValues?.project_information?.economic_activity;
        const allNaceActivities = generalInfo?.new_universal_nace_activities;
        const hasSpecialNace = isNaceCodeSelected(selectedActivityIds, allNaceActivities, targetNaceCodes);

        if (isLeasing) {
          return hasSpecialNace ? "სალიზინგო პროექტის ვადით, ს.ე.ბ.რ.გ. + 2 %" : "ლიზინგის ვადით, ს.ე.ბ.რ.გ. - 1%";
        } else {
          return hasSpecialNace ? "სესხის ვადით, ს.ე.ბ.რ.გ. ოდენობით" : "სესხის ვადით, ს.ე.ბ.რ.გ. - 3%";
        }
      },
      readonly: (formValues: Record<string, any>) => {
        const targetNaceCodes = ["38.11.0", "38.12.0", "38.21.0", "38.22.0", "38.31.0", "38.32.0"];
        const selectedActivityIds = formValues?.project_information?.economic_activity;
        const allNaceActivities = generalInfo?.new_universal_nace_activities;
        return isNaceCodeSelected(selectedActivityIds, allNaceActivities, targetNaceCodes);
      },
    },
    // მიზნობრიობის ტიპი loan_information.conditions.purpose.type
    // generalInfo?.purpose_types (name and ._id)
    {
      group: "loan_information",
      name: "loan_information.conditions.purpose.type",
      label: "მიზნობრიობის ტიპი",
      type: "select",
      options: generalInfo?.purpose_types
        ? generalInfo.purpose_types.filter((a: any) => a.name !== "მხოლოდ საოპერაციო ხარჯები").map((type: any) => ({ value: type._id, label: type.name }))
        : [],
      required: true,
    },
    {
      group: "loan_information",
      name: "loan_information.conditions.purpose.purpose_detailed",
      label: "მიზნობრიობის აღწერა",
      type: "text",
      required: true,
    },
    {
      group: "loan_information.conditions.purpose.subcategory",
      name: "loan_information.conditions.purpose.subcategory",
      label: "მიზნობრიობის ქვეკატეგორია",
      type: "autocomplete",
      isMultiField: true,
      required: true,
      options: [
        // Only add these options if NOT leasing
        ...(!isLeasing
          ? [
              {
                value:
                  "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) მიმართული იქნება ციფრული ინსტრუმენტის/გადაწყვეტის შემუშავების/შესყიდვის და დანერგვის ხარჯებისთვის.",
                label:
                  "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) მიმართული იქნება ციფრული ინსტრუმენტის/გადაწყვეტის შემუშავების/შესყიდვის და დანერგვის ხარჯებისთვის.",
              },
              {
                value: "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) მიმართული იქნება კვლევა და განვითარების (R&D) ხარჯებისთვის.",
                label: "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) მიმართული იქნება კვლევა და განვითარების (R&D) ხარჯებისთვის.",
              },
              {
                value: "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება ექსპორტზე გასატანი პროდუქციის წარმოებისათვის საჭირო ხარჯების დასაფარად.",
                label: "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება ექსპორტზე გასატანი პროდუქციის წარმოებისათვის საჭირო ხარჯების დასაფარად.",
              },
            ]
          : []),
        {
          value: "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) დააკმაყოფილებს მწვანე ტაქსონომიის მოთხოვნებს.",
          label: "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) დააკმაყოფილებს მწვანე ტაქსონომიის მოთხოვნებს.",
        },
        {
          value: "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება მაღალმთიან რეგიონში განხორციელებული ბიზნესისთვის.",
          label: "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება მაღალმთიან რეგიონში განხორციელებული ბიზნესისთვის.",
        },
        {
          value:
            "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება აგრო ტურისტული ინდუსტრიისთვის, რომელიც აკმაყოფილებს პროგრამით განსაზღვრულ მინიმალურ სავალდებულო კრიტერიუმებს.",
          label:
            "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება აგრო ტურისტული ინდუსტრიისთვის, რომელიც აკმაყოფილებს პროგრამით განსაზღვრულ მინიმალურ სავალდებულო კრიტერიუმებს.",
        },
        {
          value: "დამატებით არ არის განსაზღვრული ქვეკატეგორია",
          label: "დამატებით არ არის განსაზღვრული ქვეკატეგორია",
        },
      ],
      validation: (selectedValues: string[] | string, formValues?: Record<string, any>) => {
        const actualSubcategories = formValues?.loan_information?.conditions?.purpose?.subcategory || [];
        const subcategoriesArray = Array.isArray(actualSubcategories) ? actualSubcategories : [actualSubcategories];

        const highlandOptionValue = "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება მაღალმთიან რეგიონში განხორციელებული ბიზნესისთვის.";
        const agroTourismOptionValue =
          "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება აგრო ტურისტული ინდუსტრიისთვის, რომელიც აკმაყოფილებს პროგრამით განსაზღვრულ მინიმალურ სავალდებულო კრიტერიუმებს.";

        const selectedSettlement = formValues?.project_information?.actual_address?.mountainous_settlement;
        const isRealHighlandSettlementSelected = selectedSettlement && selectedSettlement !== "undefined";

        if (subcategoriesArray.includes(highlandOptionValue) && !isRealHighlandSettlementSelected) {
          return "ამ ოფციის ასარჩევად, პროექტის ფაქტობრივ მისამართში მითითებული უნდა იყოს მაღალმთიანი დასახლება.";
        }

        const hasHighlandOption = subcategoriesArray.includes(highlandOptionValue);
        const hasAgroTourismOption = subcategoriesArray.includes(agroTourismOptionValue);

        if ((hasHighlandOption && hasAgroTourismOption) || (hasAgroTourismOption && isRealHighlandSettlementSelected)) {
          return "აგრო ტურიზმისა და მაღალმთიანი რეგიონის ოფციებიდან შეგიძლიათ აირჩიოთ მხოლოდ ერთ-ერთი.";
        }

        // Define the component options
        const digitizationOption =
          "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) მიმართული იქნება ციფრული ინსტრუმენტის/გადაწყვეტის შემუშავების/შესყიდვის და დანერგვის ხარჯებისთვის.";
        const rdOption = "პროგრამის ფარგლებში გაცემული სესხი (სრულად ან ნაწილობრივ) მიმართული იქნება კვლევა და განვითარების (R&D) ხარჯებისთვის.";

        const hasDigitization = subcategoriesArray.includes(digitizationOption);
        const hasRD = subcategoriesArray.includes(rdOption);

        if (hasDigitization && hasRD) {
          return "გაციფრულების და კვლევა-განვითარების კომპონენტებიდან შეგიძლიათ აირჩიოთ მხოლოდ ერთ-ერთი.";
        }

        return "";
      },
      dependencies: ["project_information.actual_address.mountainous_settlement", "loan_information.conditions.purpose.subcategory"],
    },
    {
      group: "capital_expenses",
      name: "loan_information.conditions.purpose.general.amount",
      label: "თანხა",
      type: "number",
    },
    {
      group: "capital_expenses",
      name: "loan_information.conditions.purpose.general.percentage",
      label: "% წილი",
      type: "number",
      readonly: true,
      value: (formValues: Record<string, any>) => {
        // Calculate how much percentage of loan_information.conditions.approved_amount is loan_information.conditions.purpose.general.amount
        const amount = formValues.loan_information?.conditions?.purpose?.general?.amount || 0;
        const approvedAmount = formValues.loan_information?.conditions?.approved_amount || 0;
        if (approvedAmount === 0) {
          return 0;
        }
        // Return as a number with proper precision handling
        return Math.ceil((amount / approvedAmount) * 100 * 1000) / 1000;
      },
      validation: (value: number, formValues?: Record<string, any>) => {
        const generalPercentage = value || 0;
        const mixedPercentage = formValues?.loan_information?.conditions?.purpose?.mixed?.percentage || 0;
        const transportPercentage = formValues?.loan_information?.conditions?.purpose?.transport?.percentage || 0;
        const subcategoriesArray = Array.isArray(formValues?.loan_information?.conditions?.purpose?.subcategory)
          ? formValues?.loan_information?.conditions?.purpose?.subcategory
          : [formValues?.loan_information?.conditions?.purpose?.subcategory].filter(Boolean);

        if (generalPercentage < 0) {
          return "პროცენტი არ უნდა იყოს უარყოფითი";
        }

        // Check if it's an export loan
        const exportOptionValue = "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება ექსპორტზე გასატანი პროდუქციის წარმოებისათვის საჭირო ხარჯების დასაფარად.";
        const isExport = subcategoriesArray.includes(exportOptionValue);

        // Capital expenses must be at least 20% unless it's export
        if (!isExport && generalPercentage > 0 && generalPercentage < 20) {
          return "კაპიტალური ხარჯები უნდა იყოს მინიმუმ 20%";
        }

        if (generalPercentage + mixedPercentage + transportPercentage !== 100) {
          return "კაპიტალური, საბრუნავი და ტრანსპორტის ხარჯების პროცენტების ჯამი უნდა შეადგენდეს 100%-ს";
        }
        return "";
      },
      dependencies: [
        "loan_information.conditions.approved_amount",
        "loan_information.conditions.purpose.general.amount",
        "loan_information.conditions.purpose.mixed.percentage",
        "loan_information.conditions.purpose.transport.percentage",
        "loan_information.conditions.purpose.subcategory",
      ],
    },
    {
      group: "operating_expenses",
      name: "loan_information.conditions.purpose.mixed.amount",
      label: "თანხა",
      type: "number",
    },
    {
      group: "operating_expenses",
      name: "loan_information.conditions.purpose.mixed.percentage",
      label: "% წილი",
      type: "number",
      readonly: true,
      value: (formValues: Record<string, any>) => {
        // Calculate how much percentage of loan_information.conditions.approved_amount is loan_information.conditions.purpose.mixed.amount
        const amount = formValues.loan_information?.conditions?.purpose?.mixed?.amount || 0;
        const approvedAmount = formValues.loan_information?.conditions?.approved_amount || 0;
        if (approvedAmount === 0) {
          return 0;
        }
        // Return as a number with proper precision handling
        return Math.ceil((amount / approvedAmount) * 100 * 1000) / 1000;
      },
      validation: (value: number, formValues?: Record<string, any>) => {
        const mixedPercentage = value || 0;
        const generalPercentage = formValues?.loan_information?.conditions?.purpose?.general?.percentage || 0;
        const transportPercentage = formValues?.loan_information?.conditions?.purpose?.transport?.percentage || 0;
        const subcategoriesArray = Array.isArray(formValues?.loan_information?.conditions?.purpose?.subcategory)
          ? formValues?.loan_information?.conditions?.purpose?.subcategory
          : [formValues?.loan_information?.conditions?.purpose?.subcategory].filter(Boolean);

        const exportOptionValue = "პროგრამის ფარგლებში გაცემული სესხი მიმართული იქნება ექსპორტზე გასატანი პროდუქციის წარმოებისათვის საჭირო ხარჯების დასაფარად.";
        const isExport = subcategoriesArray.includes(exportOptionValue);
        const maxCap = isExport ? 100 : 80;

        if (mixedPercentage < 0 || mixedPercentage > maxCap) {
          return isExport ? `პროცენტი უნდა იყოს 0-სა და ${maxCap}-ს შორის` : `საბრუნავი საშუალებების წილი არ უნდა აღემატებოდეს ${maxCap}%-ს`;
        }
        if (generalPercentage + mixedPercentage + transportPercentage !== 100) {
          return "კაპიტალური, საბრუნავი და ტრანსპორტის ხარჯების პროცენტების ჯამი უნდა შეადგენდეს 100%-ს";
        }
        return "";
      },
      dependencies: [
        "loan_information.conditions.approved_amount",
        "loan_information.conditions.purpose.mixed.amount",
        "loan_information.conditions.purpose.subcategory",
        "loan_information.conditions.purpose.general.percentage",
        "loan_information.conditions.purpose.transport.percentage",
      ],
    },
    {
      group: "transport_expenses",
      name: "loan_information.conditions.purpose.transport.amount",
      label: "თანხა",
      type: "number",
    },
    {
      group: "transport_expenses",
      name: "loan_information.conditions.purpose.transport.percentage",
      label: "% წილი",
      type: "number",
      readonly: true,
      value: (formValues: Record<string, any>) => {
        // Calculate how much percentage of loan_information.conditions.approved_amount is loan_information.conditions.purpose.transport.amount
        const amount = formValues.loan_information?.conditions?.purpose?.transport?.amount || 0;
        const approvedAmount = formValues.loan_information?.conditions?.approved_amount || 0;
        if (approvedAmount === 0) {
          return 0;
        }
        // Return as a number with proper precision handling
        return Math.ceil((amount / approvedAmount) * 100 * 1000) / 1000;
      },
      validation: (value: number, formValues?: Record<string, any>) => {
        const transportPercentage = value || 0;
        const generalPercentage = formValues?.loan_information?.conditions?.purpose?.general?.percentage || 0;
        const mixedPercentage = formValues?.loan_information?.conditions?.purpose?.mixed?.percentage || 0;

        if (transportPercentage < 0) {
          return "პროცენტი არ უნდა იყოს უარყოფითი";
        }

        // Check if any of the restricted NACE codes are selected
        const restrictedNaceCodes = ["49.41.0", "49.42.0", "52.10.0", "52.21.0", "52.22.0", "52.23.0", "52.24.0", "52.29.0"];
        const selectedActivityIds = formValues?.project_information?.economic_activity;
        const allNaceActivities = generalInfo?.new_universal_nace_activities;
        const hasRestrictedNace = isNaceCodeSelected(selectedActivityIds, allNaceActivities, restrictedNaceCodes);

        if (hasRestrictedNace && transportPercentage > 50) {
          return "მოცემული ეკონომიკური საქმიანობისთვის ტრანსპორტის შეძენა/რემონტი არ უნდა აღემატებოდეს სესხის 50%-ს";
        }

        if (generalPercentage + mixedPercentage + transportPercentage !== 100) {
          return "კაპიტალური, საბრუნავი და ტრანსპორტის ხარჯების პროცენტების ჯამი უნდა შეადგენდეს 100%-ს";
        }

        return "";
      },
      dependencies: [
        "loan_information.conditions.approved_amount",
        "loan_information.conditions.purpose.transport.amount",
        "loan_information.conditions.purpose.general.percentage",
        "loan_information.conditions.purpose.mixed.percentage",
        "project_information.economic_activity",
      ],
    },
    {
      group: "project_information.economic_activity",
      name: "project_information.economic_activity",
      label: "ეკონომიკური საქმიანობა NACE-2 კლასიფიკატორის კოდების მიხედვით",
      type: "autocomplete",
      options: generalInfo?.new_universal_nace_activities
        ? generalInfo.new_universal_nace_activities.map((activity: any) => ({
            value: activity._id,
            label: `${activity.activity_code} ${activity.activity_description}`,
          }))
        : [],
      required: true,
      isMultiField: true, // Mark this field as representing the primitive value in the array
      validation: (value: any, formValues?: Record<string, any>) => {
        const HOTEL_PROGRAM_NAME = "სასტუმრო";
        const ALLOWED_HOTEL_CODES = ["55.10.1", "55.10.2"];
        const HOTEL_WARNING_MESSAGE =
          "შესაძლებელია მხოლოდ 55.10.1 - „ სასტუმროები და განთავსების მსგავსი საშუალებები რესტორნებით“ და 55.10.2 – „სასტუმროები და განთავსების მსგავსი საშუალებები რესტორნების გარეშე“";

        if (!formValues || !generalInfo?.universal_program_types || !generalInfo?.new_universal_nace_activities) {
          return ""; // Cannot validate without necessary info
        }

        const programComponentId = formValues.program_type;
        const selectedProgram = generalInfo.universal_program_types.find((p: any) => p._id === programComponentId);
        const isHotelProgram = selectedProgram?.name?.trim() === HOTEL_PROGRAM_NAME;

        if (!isHotelProgram) {
          return "";
        }

        const economicActivities = formValues.project_information?.economic_activity;

        if (Array.isArray(economicActivities) && economicActivities.length > 0) {
          const naceActivities = generalInfo.new_universal_nace_activities;

          const allActivitiesAllowed = economicActivities.every((activityId: string) => {
            const activity = naceActivities.find((a: any) => a._id === activityId);
            const activityCode = activity?.activity_code?.trim() ?? "";
            return activity && ALLOWED_HOTEL_CODES.includes(activityCode);
          });

          if (!allActivitiesAllowed) {
            return HOTEL_WARNING_MESSAGE;
          }
        }

        return "";
      },
      dependencies: ["program_type", "project_information.economic_activity"],
    },
    // --- Special Checkboxes Start ---
    {
      group: "special_checkboxes",
      name: "special_checkboxes.hydro_wind_solar",
      label:
        "აღნიშნული ეკონომიკური საქმიანობის ობიექტი უნდა იყოს მხოლოდ ჰიდრო/ქარის/მზის არაუმეტეს 0.5 მეგავატი დადგმული სიმძლავრის ელექტროსადგური, რომელიც არ არის ჩართული „განახლებადი წყაროებიდან ენერგიის წარმოებისა და გამოყენების მხარდაჭერის სქემისა და სიმძლავრის აუქციონის წესების დამტკიცების შესახებ“ საქართველოს მთავრობის 2022 წლის 7 დეკემბრის №556 დადგენილებით. პროგრამის ფარგლებში ელექტროენერგიის წარმოებიდან მიღებულ შემოსავლად მიიჩნევა „ენერგეტიკისა და წყალმომარაგების შესახებ“ საქართველოს კანონით განსაზღვრული მიკროსიმძლავრის  ელექტროსადგურის დროებით მფლობელობაში/ სარგებლობაში გადაცემის შედეგად მიღებული შემოსავალი.",
      type: "checkbox",
      required: true,
      visibility: (data) =>
        isNaceCodeSelected(data?.project_information?.economic_activity, generalInfo?.new_universal_nace_activities, ["35.11.1", "35.11.2", "35.11.3"]),
      dependencies: ["project_information.economic_activity"],
    },
    {
      group: "special_checkboxes",
      name: "special_checkboxes.transport_m3",
      label:
        "აღნიშნული ეკონომიკური საქმიანობის ფარგლებში დასაშვებია მხოლოდ „ტექნიკური რეგლამენტის „სხვადასხვა კატეგორიის ავტოსატრანსპორტო საშუალებების ინსპექტირების პერიოდულობისა და საფასურების“ დამტკიცების თაობაზე“ საქართველოს მთავრობის 2014 წლის 3 იანვრის №37 დადგენილებით განსაზღვრული M3 კატეგორიის 2013 წელს ან მის შემდეგ გამოშვებული ავტოსატრანსპორტო საშუალების შეძენა/რემონტი. აღნიშნული ეკონომიკური საქმიანობით დაშვებულია მხოლოდ საქართველოს ტერიტორიის ფარგლებში რეგულარული სამგზავრო გადაყვანა (გარდა მუნიციპალიტეტის ადმინისტრაციულ საზღვრებში რეგულარული სამგზავრო გადაყვანისა), რომელიც აკმაყოფილებს „საავტომობილო ტრანსპორტის შესახებ“ საქართველოს კანონითა და ამ საქმიანობასთან დაკავშირებული სხვა სამართლებრივი აქტებით  განსაზღვრულ მოთხოვნებს.",
      type: "checkbox",
      required: true,
      visibility: (data) => isNaceCodeSelected(data?.project_information?.economic_activity, generalInfo?.new_universal_nace_activities, ["49.39.0"]),
      dependencies: ["project_information.economic_activity"],
    },
    {
      group: "special_checkboxes",
      name: "special_checkboxes.shipping",
      label:
        "დასახელებულ ეკონომიკურ საქმიანობაში დასაშვებია მხოლოდ საქართველოს საზღვაო კოდექსით განსაზღვრული საქართველოს სახელმწიფოს დროშის ქვეშ მცურავი სატვირთო ან/და სატვირთო-სამგზავრო, დიდი გემის შეძენა/რემონტი;",
      type: "checkbox",
      required: true,
      visibility: (data) =>
        isNaceCodeSelected(data?.project_information?.economic_activity, generalInfo?.new_universal_nace_activities, ["50.10.0", "50.20.0"]),
      dependencies: ["project_information.economic_activity"],
    },
    // --- Special Checkboxes End ---
    {
      group: "project_information_general",
      name: "project_information.project_type",
      type: "select",
      label: "პროექტის ტიპი",
      options: projectTypes.map((type) => ({ value: type, label: type })),
      required: true,
    },
    //ტერიტორიის სარგებლობის ტიპი project_information.territory_usage_typ
    {
      group: "project_information_general",
      name: "project_information.territory_usage_type",
      label: "ტერიტორიის სარგებლობის ტიპი",
      type: "select",
      options: territoryUsageTypes.map((type) => ({ value: type, label: type })),
      required: true,
    },
    // პროექტის ჯამური ინვესტიცია (სესხი + პირადი ინვესტიცია) project_information.total_investment
    {
      group: "project_information_general",
      name: "project_information.total_investment",
      label: "პროექტის ჯამური ინვესტიცია (სესხი + პირადი ინვესტიცია)",
      type: "number",
      required: true,
    },
    // ფუნქციონირების დაწყების ან გაფართოება/გადაიარაღების დასრულების სავარაუდო ვადა (თვე) - project_information.completion_time
    {
      group: "project_information_general",
      name: "project_information.completion_time",
      label: "ფუნქციონირების დაწყების ან გაფართოება/გადაიარაღების დასრულების სავარაუდო ვადა (თვე)",
      type: "number",
      required: true,
      inputProps: { step: "1", min: "0" },
    },
    // Agro Tourism Fields
    {
      group: "agro_tourism",
      name: "agro_tourism_information.placement",
      label: "განთავსება",
      type: "checkbox",
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.catering",
      label: "კვებითი მომსახურება",
      type: "checkbox",
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.educational",
      label: "საგანმანათლებლო/შემეცნებითი",
      type: "checkbox",
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.recreational",
      label: "გასართობი/რეკრეაციული",
      type: "checkbox",
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.other_activity_enabled", // Renamed to avoid conflict and indicate purpose
      label: "სხვა აქტივობა",
      type: "checkbox",
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.other_activity",
      label: "სხვა აქტივობა",
      type: "text",
      visibility: (data: any) => data.agro_tourism_information?.other_activity_enabled === true,
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.farming_property",
      label: "საკუთრების ან მართლზომიერი სარგებლობის უფლებით ფლობს ფერმერულ მეურნეობას*",
      type: "checkbox",
      required: true,
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.food_safety",
      label:
        "სურსათის/ცხოველის საკვების უვნებლობის, ვეტერინარიისა და მცენარეთა დაცვის კოდექსით განსაზღვრული მოთხოვნების შესაბამისად, რეგისტრირებულია ბიზნესოპერატორად*",
      type: "checkbox",
      required: true,
    },
    {
      group: "agro_tourism",
      name: "agro_tourism_information.registered_farm",
      label: "რეგისტრირებულია საქართველოს გარემოს დაცვისა და სოფლის მეურნეობის სამინისტროს ფერმათა/ფერმერთა რეესტრში*",
      type: "checkbox",
      required: true,
    },
    // --- Hotel Information Fields Start ---
    {
      group: "project_information_hotel_information_industrial_hotel_component",
      name: "project_information.hotel_information.existing_rooms_amount",
      label: "არსებული ნომრების რაოდენობა",
      type: "number",
      required: true,
      inputProps: { step: "1", min: "0" },
    },
    {
      group: "project_information_hotel_information_industrial_hotel_component",
      name: "project_information.hotel_information.additional_rooms_amount",
      label: "პროექტის ფარგლებში დამატებული ახალი ნომრების რაოდენობა",
      type: "number",
      required: true,
      inputProps: { step: "1", min: "0" },
      description: (data: any) =>
        isNaceCodeSelected(data?.project_information?.economic_activity, generalInfo?.new_universal_nace_activities, ["55.20.0"])
          ? "აღნიშნული ეკონომიკური საქმიანობის ფარგლებში სესხით/ლიზინგით სარგებლობისათვის სავალდებულოა ობიექტი იყოს არაუმეტეს 10 ნომრიანი/ერთეული დასასვენებელი და სხვა მოკლევადიანი განთავსების საშუალება."
          : "არსებული ნომრების და ახალი ნომრების რაოდენობის ჯამი უნდა იყოს 10 ან 10-ზე მეტი.",
      validation: (value: number, formValues?: Record<string, any>) => {
        if (value < 0) {
          return "მნიშვნელობა არ უნდა იყოს უარყოფითი";
        }
        if (!formValues) return "";
        const existingRooms = Number(formValues.project_information?.hotel_information?.existing_rooms_amount) || 0;
        const additionalRooms = Number(value) || 0;
        const totalRooms = existingRooms + additionalRooms;

        if (isNaceCodeSelected(formValues?.project_information?.economic_activity, generalInfo?.new_universal_nace_activities, ["55.20.0"])) {
          if (totalRooms > 10) {
            return "ჯამური ნომრების რაოდენობა (არსებული + ახალი) არ უნდა აღემატებოდეს 10-ს (საქმიანობა 55.20.0)";
          }
        } else {
          if (totalRooms < 10) {
            return "ჯამური ნომრების რაოდენობა (არსებული + ახალი) უნდა იყოს 10 ან მეტი";
          }
        }
        return "";
      },
      dependencies: ["project_information.hotel_information.existing_rooms_amount", "project_information.economic_activity"],
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.criteria.permit_given",
      label: "სამშენებლო ნებართვა/ბრძანება გაცემულია სასტუმროს/სასტუმრო კომპლექსის მშენებლობა/რეკონსტრუქციისთვის",
      type: "checkbox",
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.criteria.permit_number",
      label: "ნებართვის/ბრძანების ნომერი",
      type: "text",
      visibility: (data: any) => data.project_information?.hotel_information?.criteria?.permit_given === true,
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.criteria.permit_organization",
      label: "ნებართვის გამცემი ორგანო",
      type: "text",
      visibility: (data: any) => data.project_information?.hotel_information?.criteria?.permit_given === true,
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.criteria.permit_absence_reason",
      label: "კომენტარი ნებართვის არარსებობის შესახებ",
      type: "textarea",
      visibility: (data: any) => data.project_information?.hotel_information?.criteria?.permit_given === false,
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.satisfies_criteria",
      label: "სასტუმროს ნომრები აკმაყოფილებს პროგრამის კრიტერიუმებს",
      type: "checkbox",
      required: true,
      description:
        "სტუმართა რეგისტრაცია (რეცეფცია), რომელსაც თავის მხრივ აქვს: განცალკევებული, დამოუკიდებელი რეგისტრაციის ადგილი; სანიტარიული კვანძი მისაღებ სივრცეში.",
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.has_bathroom",
      label: "სასტუმროს ნომრები უზრუნველყოფილია სააბაზანო/სანიტარიული კვანძით",
      type: "checkbox",
      required: true,
      description: "ცენტრალური ან/და ინდივიდუალური კონდიცირებით (გათბობის/გაგრილების სისტემა), აღჭურვილი ინდივიდუალური მართვის საშუალებებით.",
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.elevator",
      label: "ლიფტი - თუ შენობა 2-ზე მეტსართულიანია",
      type: "checkbox",
    },
    {
      group: "project_information_hotel_information",
      name: "project_information.hotel_information.elevator_absence_reason",
      label: "კომენტარი ლიფტის არარსებობის შემთხვევაში",
      type: "textarea",
      visibility: (data: any) => data.project_information?.hotel_information?.elevator === false,
    },
    // --- Hotel Information Fields End ---

    // --- Attachment Fields Start ---
    {
      group: "attachments",
      name: "attachments.documents",
      label: "დოკუმენტი",
      type: "file",
    },
    // --- Attachment Fields End ---
  ],
});

export default getFormConfig;
